import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import TeamLogo from "../../../components/TeamLogo";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import type { Match } from "../../logger/types";
import { AnimatedNumber } from "../animation/AnimatedNumber";
import { useLiveScore } from "../../logger/hooks/useLiveScore";
import { contrastRatio, lightenForContrast } from "../../../lib/teamPalette";
import { computeScoreboardClock, PERIOD_LABEL_KEY } from "../clock";

interface Props {
  match: Match;
  events: MatchEvent[];
  getPlaybackNowMs: () => number;
  options: Record<string, unknown>;
}

const DARK_PANEL = "rgba(8, 15, 30, 0.88)";

export default function Scoreboard({
  match,
  events,
  getPlaybackNowMs,
  options,
}: Props) {
  const { t } = useTranslation("broadcast");
  const wide = options.variant !== "compact";
  const showLogos = options.show_logos !== false;
  const showClock = options.show_clock !== false;
  const [nowMs, setNowMs] = useState(getPlaybackNowMs);
  useEffect(() => {
    const id = setInterval(() => setNowMs(getPlaybackNowMs()), 500);
    return () => clearInterval(id);
  }, [getPlaybackNowMs]);

  const { liveScore } = useLiveScore(match, events);
  const addedRef = useRef(0);
  const { clock, phase } = useMemo(
    () => computeScoreboardClock(match, events, nowMs, addedRef.current),
    [match, events, nowMs],
  );
  useEffect(() => {
    if (clock.addedSeconds) addedRef.current = clock.addedSeconds;
  }, [clock.addedSeconds]);

  const periodKey = PERIOD_LABEL_KEY[phase];
  const home = match.home_team;
  const away = match.away_team;
  const accent = (c?: string | null, fallback = "#38bdf8") => c || fallback;
  // EPIC-112: a kit shirt colour sits on a dark overlay, so a near-black kit is
  // lightened until it stays visible. With no kit the team colour is used
  // exactly as before (PMA-BCAST-004 AC18).
  const homeAccent = (() => {
    const kitColor = home.kit?.shirt_color;
    if (!kitColor) return accent(home.primary_color);
    const ratio = contrastRatio(kitColor, "#080f1e");
    return ratio != null && ratio < 3
      ? lightenForContrast(kitColor, "#080f1e", 3)
      : kitColor;
  })();

  return (
    <div
      data-testid="broadcast-scoreboard"
      data-home-accent={homeAccent}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "0.6em",
        padding: "0 0.9em",
        boxSizing: "border-box",
        background: DARK_PANEL,
        color: "#ffffff",
        borderRadius: "0.5em",
        borderBottom: `0.18em solid ${homeAccent}`,
        fontSize: "1em",
        lineHeight: 1.1,
      }}
    >
      <TeamSide team={home} wide={wide} align="left" showLogo={showLogos} />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5em",
          fontWeight: 800,
          fontSize: "2.25em",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <span data-testid="broadcast-score-home">
          <AnimatedNumber value={liveScore.home} />
        </span>
        <span style={{ opacity: 0.6 }}>-</span>
        <span data-testid="broadcast-score-away">
          <AnimatedNumber value={liveScore.away} />
        </span>
      </div>
      <TeamSide team={away} wide={wide} align="right" showLogo={showLogos} />
      {showClock && (
        <div
          style={{
            textAlign: "center",
            minWidth: "6em",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          <div
            data-testid="broadcast-clock"
            style={{ fontWeight: 800, fontSize: "1.75em" }}
          >
            {clock.mainClock}
            {clock.addedClock && (
              <span style={{ color: "#fbbf24", marginLeft: "0.25em" }}>
                {clock.addedClock}
              </span>
            )}
          </div>
          {periodKey && (
            <div
              data-testid="broadcast-period"
              style={{
                fontSize: "1.25em",
                fontWeight: 700,
                opacity: 1,
                letterSpacing: "0.05em",
                marginTop: "0.1em",
              }}
            >
              {t(`period.${periodKey}`)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TeamSide({
  team,
  wide,
  align,
  showLogo,
}: {
  team: Match["home_team"];
  wide: boolean;
  align: "left" | "right";
  showLogo: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.5em",
        flexDirection: align === "left" ? "row" : "row-reverse",
        minWidth: 0,
      }}
    >
      {showLogo && <TeamLogo team={team} size={48} />}
      <span
        style={{
          fontWeight: 700,
          fontSize: "1.5em",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {wide ? team.name || team.short_name : team.short_name}
      </span>
    </div>
  );
}
